import type { ResourceListItem, ScopeResponse } from "@k8s-dashboard/contracts";
import type { StoredColumn } from "@/app/preference-storage";

export const MAX_RETAINED_PAGES = 4;
export const PAGE_SIZE = 50;
export const MAX_RETAINED_ROWS = MAX_RETAINED_PAGES * PAGE_SIZE;
export const MAX_SELECTION = 200;
export const TABLE_ROW_HEIGHT = 32;
export const TABLE_HEIGHT = 420;
export const TABLE_OVERSCAN = 8;

export type ColumnKind = "select" | "name" | "namespace" | "printer" | "created";
export type ExplorerColumn = Readonly<{
  id: string;
  label: string;
  kind: ColumnKind;
  width: number;
  min: number;
  max: number;
  required: boolean;
  printerIndex?: number;
}>;

const fixed = {
  select: { width: 44, min: 44, max: 44 },
  name: { width: 240, min: 160, max: 480 },
  namespace: { width: 160, min: 120, max: 320 },
  printer: { width: 160, min: 96, max: 480 },
  created: { width: 200, min: 160, max: 280 },
} as const;

export function printerColumnId(name: string, ordinal: number): string {
  return `printer:${new TextEncoder().encode(name).byteLength}:${name}:${ordinal}`;
}

export function buildExplorerColumns(namespaced: boolean, printerNames: readonly string[], labels: {
  select: string; name: string; namespace: string; created: string;
}): ExplorerColumn[] {
  const ordinals = new Map<string, number>();
  const columns: ExplorerColumn[] = [
    { id: "select", label: labels.select, kind: "select", ...fixed.select, required: true },
    { id: "name", label: labels.name, kind: "name", ...fixed.name, required: true },
  ];
  if (namespaced) columns.push({ id: "namespace", label: labels.namespace, kind: "namespace", ...fixed.namespace, required: false });
  printerNames.forEach((name, printerIndex) => {
    const ordinal = ordinals.get(name) ?? 0;
    ordinals.set(name, ordinal + 1);
    columns.push({
      id: printerColumnId(name, ordinal), label: name, kind: "printer", ...fixed.printer,
      required: false, printerIndex,
    });
  });
  columns.push({ id: "created", label: labels.created, kind: "created", ...fixed.created, required: false });
  return columns;
}

export function sanitizeColumnSettings(columns: readonly ExplorerColumn[], stored?: readonly StoredColumn[]): StoredColumn[] {
  const byId = new Map((stored ?? []).slice(0, 64).map((column) => [column.id, column]));
  return columns.slice(0, 64).map((column) => {
    const saved = byId.get(column.id);
    return {
      id: column.id,
      width: column.kind === "select" ? column.width : Math.min(column.max, Math.max(column.min, Math.round(saved?.width ?? column.width))),
      visible: column.required ? true : saved?.visible !== false,
    };
  });
}

export { scopeFingerprint } from "@/state/namespaceSelection";

/** 현재 Scope가 선택한 cluster/namespace 조회를 허용하는지 확인합니다.
 * `all`은 서버가 허용 namespace 집합으로 다시 제한하므로 접근 가능한 cluster면 허용합니다. */
export function scopeAllowsNamespace(
  scope: ScopeResponse | undefined,
  clusterId: string,
  namespace: string,
): boolean {
  const cluster = scope?.clusters.find((item) => item.id === clusterId);
  if (!cluster?.accessible) return false;
  if (!namespace || namespace === "all" || cluster.namespaces === "all") return true;
  return cluster.namespaces.includes(namespace);
}

export function selectionIdentity(input: {
  partition?: string; clusterId: string; gvr: string; scopeFingerprint: string;
  namespace: string; namePrefix: string; labelSelector: string; order: string;
}): string {
  return [input.partition ?? "ephemeral", input.clusterId, input.gvr, input.scopeFingerprint,
    input.namespace, input.namePrefix, input.labelSelector, input.order]
    .map((value) => `${value.length}:${value}`).join("|");
}

export function dedupeRows<T extends { item: ResourceListItem }>(rows: readonly T[]): { rows: T[]; duplicateCount: number } {
  const seen = new Set<string>();
  const unique: T[] = [];
  let duplicateCount = 0;
  for (const row of rows.slice(0, MAX_RETAINED_ROWS)) {
    if (seen.has(row.item.uid)) {
      duplicateCount += 1;
      continue;
    }
    seen.add(row.item.uid);
    unique.push(row);
  }
  return { rows: unique, duplicateCount };
}
